import { LootProfileSchema, rollEquipment } from '../rules/loot.js';
import { BIOMES } from './biomes.js';
import { LOOT_POOLS, LOOT_PROFILES } from './loot.js';
import { MOBS } from './mobs.js';
import { invaderOf, warlordOf } from './event-mobs.js';
import { UPGRADE_MATERIALS } from './upgrade-materials.js';
/**
 * The world events (ADR 0050): one every two hours on the hour, a warning
 * five minutes before, one at a time for the whole server. Data, not code
 * (CLAUDE.md rule 4): what happens in each slot, how long it lasts, what it
 * pays. The pure rules are in `rules/world-events.ts`.
 */
export const WORLD_EVENT_KINDS = ['invasion', 'giant', 'convoy'];
/** One event every two hours, starting on the hour. */
export const EVENT_SLOT_MS = 2 * 60 * 60_000;
/** Told to everyone this long before it starts: time to cross the valley. */
export const EVENT_WARNING_MS = 5 * 60_000;
/** A game master's event (ADR 0025) waits this long: enough to read the line. */
export const EVENT_GM_WARNING_MS = 10_000;
/** French, for the announcements, the tracker and the list of the next ones. */
export const WORLD_EVENT_NAMES = Object.freeze({
    invasion: 'Invasion gobeline',
    giant: 'Élite géante',
    convoy: 'Chariot d’or',
});
/**
 * What each slot holds, in turn: the whole rotation comes back every
 * `EVENT_ROTATION.length` slots. The three scenarios follow one another, never
 * the same twice in a row (the last and the first included); fifteen slots
 * over twelve a day, so a region's event never falls at the same hour two
 * days running. The cart sets out from the capital, so the meadow has none.
 */
export const EVENT_ROTATION = Object.freeze([
    { kind: 'invasion', biome: 'meadow' },
    { kind: 'giant', biome: 'forest' },
    { kind: 'convoy', biome: 'swamp' },
    { kind: 'invasion', biome: 'desert' },
    { kind: 'giant', biome: 'snow' },
    { kind: 'convoy', biome: 'forest' },
    { kind: 'invasion', biome: 'swamp' },
    { kind: 'giant', biome: 'meadow' },
    { kind: 'convoy', biome: 'desert' },
    { kind: 'invasion', biome: 'snow' },
    { kind: 'giant', biome: 'swamp' },
    { kind: 'convoy', biome: 'snow' },
    { kind: 'invasion', biome: 'forest' },
    { kind: 'giant', biome: 'desert' },
    { kind: 'convoy', biome: 'forest' },
]);
/**
 * The goblin invasion. Four waves march on the square from the four
 * points of the compass, goblin raiders driving the region's own creatures
 * before them, the last with the warlord; the next one sets out when the
 * last has fallen, or after `waveGapMs` at the latest. The warlord's fall
 * before `durationMs` saves the village.
 */
export const INVASION = Object.freeze({
    durationMs: 12 * 60_000,
    waves: 4,
    /** A breath between a wave that fell and the next. */
    wavePauseMs: 15_000,
    waveGapMs: 3 * 60_000,
    /** Raiders of a wave: `base`, plus `perDefender` for every player about, up to `cap`. */
    raiders: { base: 3, perDefender: 1.5, cap: 24 },
    /** The share of a wave that is the region's creatures rather than goblins. */
    creatureShare: 0.5,
    /**
     * Each defender beyond the first makes every assailant tougher: this share
     * more health and more damage, up to `max` times. The warlord's health
     * follows `warlord` instead, his blows these.
     */
    might: {
        health: { perDefender: 0.15, max: 3 },
        damage: { perDefender: 0.05, max: 1.5 },
    },
    /** The last wave's escort beside the warlord, as a share of a wave. */
    escortShare: 0.5,
    /** Each defender beyond the first adds this share of the warlord's health, up to `maxScale` times. */
    warlord: { perDefender: 0.5, maxScale: 6 },
    /** Tiles past the village's edge the waves come from. */
    approach: 5,
    /** Tiles around the village that count as being there: the defenders, the music. */
    reach: 12,
});
/**
 * The giant: the biome's legendary elite twice its size, prowling round the
 * village from one point of the compass to the next. Its fall before
 * `durationMs` is the victory; otherwise it wanders off.
 */
export const GIANT = Object.freeze({
    durationMs: 10 * 60_000,
    /** Tiles past the village's edge its round runs. */
    ring: 8,
    /** It stands this long at each point of its round, for the slow to catch up. */
    pauseMs: 20_000,
    /** Each fighter beyond the first adds this share of its health, up to `maxScale` times. */
    health: { perFighter: 0.6, maxScale: 10 },
    /** Tiles around it that count as being there: the fighters, the music. */
    reach: 14,
});
/**
 * The gold cart: from the capital's edge to the biome's village along the
 * road, at a walk. It only moves with someone beside it and no raider near;
 * goblin raiders, the region's creatures before them, spring at it in
 * waves. Its arrival before `durationMs` is the victory.
 */
export const CONVOY = Object.freeze({
    durationMs: 10 * 60_000,
    /** Tiles from the cart that count as escorting it, and earn escort points. */
    escortRange: 5,
    /** A raider this close holds the cart up. */
    blockRange: 5,
    /** Tiles of road it sets out on at a time: the next stretch once this one is walked. */
    stretch: 10,
    waves: 6,
    /** The first wave springs this long after the start, the next ones `waveGapMs` apart. */
    firstWaveMs: 20_000,
    waveGapMs: 35_000,
    /** Tiles of road ahead of the cart a wave springs from. */
    ambushAhead: 12,
    /** Raiders of a wave: `base`, plus `perEscort` for every escort, up to `cap`. */
    raiders: { base: 4, perEscort: 2, cap: 20 },
    /** Tiles around the cart that count as being there: the escorts, the music. */
    reach: 14,
    /** One road tile in this many is sent to the clients to draw the way; the last always is. */
    roadSampleEvery: 3,
});
/** How many times a raider's experience each kind pays a fighter over the threshold. */
const REWARD_XP_FACTOR = Object.freeze({
    invasion: 12,
    giant: 12,
    // The cart pays gold beside it.
    convoy: 10,
});
/**
 * What an event pays each fighter over the threshold, in experience: a
 * dozen raiders' worth for an invasion or a giant, a little less for the
 * cart, which pays gold too. Half again on the podium.
 */
export function eventRewardXp(kind, biome, podium) {
    const base = MOBS[invaderOf(biome)].xp * REWARD_XP_FACTOR[kind];
    return Math.round(podium ? base * PODIUM_XP : base);
}
/**
 * The most gold one fighter takes from one event, whatever the data says:
 * `EconomyService` holds every payment to it (ADR 0050).
 */
export const EVENT_GOLD_CAP = 250;
/** The cart's gold per escort over the threshold, by the village it reaches: about a daily contract. */
export const CONVOY_GOLD = Object.freeze({
    meadow: 40,
    forest: 50,
    swamp: 80,
    desert: 110,
    snow: 160,
});
/** The gold an event pays one fighter over the threshold: only the cart pays any, capped. */
export function eventGold(kind, biome, podium) {
    if (kind !== 'convoy')
        return 0;
    const base = CONVOY_GOLD[biome];
    return Math.min(EVENT_GOLD_CAP, Math.round(podium ? base * PODIUM_XP : base));
}
/** The podium's experience (and gold) against the rest. */
export const PODIUM_XP = 1.5;
/**
 * An event's loot, per defender over the threshold, for them alone: two of
 * the biome's rare material and a piece of its gear, better than a chest's;
 * the podium's piece is a zone boss's, with the biome's relic beside it.
 */
export const EVENT_LOOT = Object.freeze({
    rareMaterial: 2,
    profile: LootProfileSchema.parse({
        chance: 1,
        weights: { common: 30, rare: 55, epic: 13, legendary: 2 },
    }),
    podium: { profile: LOOT_PROFILES['zone-boss'], relic: 1 },
});
export function rollEventLoot(biome, podium, rng) {
    const materials = UPGRADE_MATERIALS[biome];
    const drops = [{ templateId: materials.rare.id, quantity: EVENT_LOOT.rareMaterial }];
    const profile = podium ? EVENT_LOOT.podium.profile : EVENT_LOOT.profile;
    const piece = rollEquipment(profile, LOOT_POOLS[biome], rng);
    if (piece !== undefined)
        drops.push(piece);
    if (podium)
        drops.push({ templateId: materials.epic.id, quantity: EVENT_LOOT.podium.relic });
    return drops;
}
/**
 * Who invades this biome's village: the goblin raiders, the creatures of
 * the biome's dens they drive before them, and the warlord of the last wave.
 */
export function invasionForces(biome) {
    return {
        raider: invaderOf(biome),
        warlord: warlordOf(biome),
        creatures: BIOMES[biome].dens.map((den) => den.mob),
    };
}
//# sourceMappingURL=world-events.js.map